import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'

import { TelemetryDataContext, computeTelemetryStats } from '../../instructorDashboard/context/TelemetryDataContext'
import ClassCodingTimeline from '../../instructorDashboard/components/ClassCodingTimeline'
import TimelineLegend from '../../instructorDashboard/components/TimelineLegend'
import LatestConversation from '../../instructorDashboard/components/LatestConversation'
import {
  card,
  cardHeader,
  cardSubtitle,
  cardTitle,
  dashboardPage,
  toolbarButton,
} from '../../instructorDashboard/components/dashboardStyles'
import '../../../css/instructorDash/instructorDashboard.scss'
import { testsEnabled } from '../sessionSettings'
import ExperimentExportButtons from './ExperimentExportButtons'
import { errorLabel, presentableCode } from './experimentPresenter'
import StudentConversation from './StudentConversation'
import StudentReplay from './StudentReplay'
import { useConditionTelemetry } from './useConditionTelemetry'
import {
  aiUsePatterns,
  classStatus,
  commonErrors,
  errorKey,
  filteredText,
  latestEvent,
  mergeConditions,
  resultTests,
  runFiles,
  runOutcome,
  studentErrors,
  studentLabel,
  studentConversation,
  studentNumbers,
  studentPatterns,
  studentsByNeed,
  stuckTests,
} from './experimentOverviewStats'

const percent = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')

const formatMinutes = (seconds) => (seconds ? `${Math.max(1, Math.round(seconds / 60))} min` : '—')

// How long before their telemetry was fetched the student was last active (studentsByNeed's
// idleMs, the Inactive flag's measure), not before now: after the page was hidden, the data
// can be minutes old until the next poll answers.
const formatLastActive = (iso, idleMs) => {
  if (!iso) return '—'
  if (idleMs == null) return new Date(iso).toLocaleString()
  const minutesAgo = Math.round(idleMs / 60000)
  if (minutesAgo < 1) return 'just now'
  if (minutesAgo < 60) return `${minutesAgo} min ago`
  return new Date(iso).toLocaleString()
}

// When something happened (an error, a run, a test result): under an hour, the same way, as
// of when that student's telemetry was fetched; then the time of day ("1:44 PM", with the day
// when not today) - short, beside an error's message on the Common errors card.
const formatWhen = (timestamp, fetchedAt) => {
  const minutesAgo = fetchedAt ? Math.round((fetchedAt - timestamp) / 60000) : null
  if (minutesAgo != null && minutesAgo < 1) return 'just now'
  if (minutesAgo != null && minutesAgo < 60) return `${minutesAgo} min ago`
  const date = new Date(timestamp)
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return date.toDateString() === new Date().toDateString()
    ? time
    : `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`
}

// How long something has gone on: 'under a minute', '12 min', '1 h 5 min'.
const formatSpan = (ms) => {
  const minutes = Math.round(ms / 60000)
  if (minutes < 1) return 'under a minute'
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// A test result's score (TEST_CASE_RESULT's payload): [passed, total].
const score = (event) => [event?.payload?.passed_count || 0, event?.payload?.total_tests || 0]

// A student's Tests passed by their latest test result (latestEvent's), '—' when they have
// none: in their detail, and in the Students list for a student who has just started, whom the
// Monitor's figures (the list's for the rest) do not count yet.
const testsPassed = (result) => (result ? score(result.event).join(' / ') : '—')

const statLabel = { fontSize: '13px', color: '#9ca3af' }
const statValue = { fontSize: '28px', fontWeight: 700, color: '#111', lineHeight: 1.2 }
const statNote = { fontSize: '13px', fontWeight: 400, color: '#9ca3af', marginLeft: '6px' }
const cellSx = { borderBottom: '1px solid #f3f4f6', fontSize: '14px' }
// Every column but the flags keeps to one line; the flags wrap.
const oneLineSx = { ...cellSx, whiteSpace: 'nowrap' }
const headSx = { ...oneLineSx, color: '#6b7280', fontWeight: 600, background: 'white' }
// The app's global table rules (codeIssuePanel.scss: thead, th, td) draw heavy outer and
// column borders; the Monitor's lists use light row lines only.
const tableSx = {
  border: 'none',
  '& thead': { border: 'none', textAlign: 'left' },
  '& tr': { border: 'none' },
  '& th, & td': { borderLeft: 'none', borderRight: 'none', borderTop: 'none' },
}
// A card's empty state, and a dialog's note in place of what it has none of.
const emptyText = { margin: 0, color: '#9ca3af', fontSize: '14px' }
const dialogNote = { margin: 0, fontSize: '14px', color: '#6b7280' }
// A section's heading in a dialog.
const sectionLabel = { ...statLabel, margin: '20px 0 6px 0' }

const chip = { fontSize: '12px', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap', border: '1px solid' }
// A flag's colour by its weight (experimentOverviewStats.studentsByNeed): red for stuck,
// amber for the rest; green for a student who passed every test.
const flagChip = (weight) =>
  weight >= 3
    ? { ...chip, color: '#991b1b', background: '#fee2e2', borderColor: '#fecaca' }
    : { ...chip, color: '#92400e', background: '#fef3c7', borderColor: '#fde68a' }
const doneChip = { ...chip, color: '#065f46', background: '#d1fae5', borderColor: '#a7f3d0' }
const mono = 'Monaco, Menlo, monospace'
// A row of a card's list that opens in place (Common errors, Tests students are stuck on), or
// one of a student's errors in their detail: a button laid out as a list row.
const listRowSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '9px 4px',
  border: 'none',
  borderBottom: '1px solid #f3f4f6',
  background: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { background: '#f9fafb' },
}
// A list row's text (an error's key, a test's name): on one line, cut short, until it opens.
const rowText = (open, extra = {}) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '13px',
  color: '#111',
  ...(open
    ? { whiteSpace: 'normal', wordBreak: 'break-word' }
    : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  ...extra,
})
const rowNote = { fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }
const codeText = { margin: 0, fontFamily: mono, fontSize: '12px', lineHeight: '19px', whiteSpace: 'pre', tabSize: 4 }
const errorText = {
  ...codeText,
  maxHeight: '30vh',
  overflow: 'auto',
  padding: '10px 14px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: '#991b1b',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
}

const Stat = ({ label, children }) => (
  <div>
    <div style={statLabel}>{label}</div>
    <div style={statValue}>{children}</div>
  </div>
)

// studentPatterns on the merged telemetry, one student at a time, keeping a student's last
// result while their events are unchanged: each poll brings only what is new
// (useConditionTelemetry), and only a student with new events is classified again.
const usePatternsById = (eventsByUser) => {
  const cache = useRef(new Map())
  return useMemo(() => {
    const next = new Map()
    const patternsById = {}
    Object.entries(eventsByUser).forEach(([id, events]) => {
      const signature = `${events.length}:${events[events.length - 1]?.timestamp}`
      const kept = cache.current.get(id)
      const entry = kept && kept.signature === signature ? kept : { signature, value: studentPatterns(events) }
      next.set(id, entry)
      if (entry.value) patternsById[id] = entry.value
    })
    cache.current = next
    return patternsById
  }, [eventsByUser])
}

const numbersStorageKey = (experimentId) => `vizpi.experimentOverview.studentNumbers.${experimentId}`

// The numbers this browser gave an experiment's students before, or none.
const storedNumbers = (experimentId) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(numbersStorageKey(experimentId)) || '{}')
    return Object.fromEntries(Object.entries(stored || {}).filter(([, number]) => Number.isInteger(number) && number > 0))
  } catch (error) {
    // Storage blocked or unreadable: the numbers start afresh.
    return {}
  }
}

// The students' numbers (studentNumbers), kept as given: from poll to poll, and in this
// browser's storage, per experiment, from one visit to the next. A student who started earlier
// but shows up later (their first telemetry batch still on its way at a poll, a condition
// whose requests failed) then keeps the next number after a reload too, instead of everyone
// after them moving up by one.
const useStudentNumbers = (experimentId, eventsByUser) => {
  const kept = useRef(null)
  const numbers = useMemo(() => {
    if (kept.current?.experimentId !== experimentId) {
      kept.current = { experimentId, numbers: storedNumbers(experimentId) }
    }
    kept.current.numbers = studentNumbers(eventsByUser, kept.current.numbers)
    return kept.current.numbers
  }, [experimentId, eventsByUser])
  useEffect(() => {
    try {
      window.localStorage.setItem(numbersStorageKey(experimentId), JSON.stringify(numbers))
    } catch (error) {
      // Storage full or blocked: the numbers still hold while the page stays open.
    }
  }, [experimentId, numbers])
  return numbers
}

// Until the first data arrives: a Monitor component's box and title with the stat tiles'
// spinner, instead of the component itself showing an empty class ("No error data yet").
const Pending = ({ className, title }) => (
  <div className={className} style={{ height: '100%' }}>
    <h2>{title}</h2>
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={20} />
    </div>
  </div>
)

// One AI-use pattern: its count, and a bar for its share of the students with AI chat.
const PatternRow = ({ label, count, total }) => (
  <div style={{ marginBottom: '10px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: count ? '#111' : '#9ca3af' }}>
      <span>{label}</span>
      <span style={{ fontWeight: 600 }}>{count}</span>
    </div>
    <div style={{ height: '6px', borderRadius: '3px', background: '#f3f4f6', marginTop: '4px', overflow: 'hidden' }}>
      <div style={{ width: total ? `${(count / total) * 100}%` : 0, height: '100%', background: '#8B5CF6' }} />
    </div>
  </div>
)

// A run's code, read-only, through the word filter (filteredText, which keeps its lines), with
// line numbers that stay in view when a long line scrolls the code sideways.
const CodeView = ({ code }) => {
  const lines = filteredText(code).replace(/\r\n?/g, '\n').split('\n')
  return (
    <div style={{ maxHeight: '45vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
      <div style={{ display: 'flex', width: 'max-content', minWidth: '100%' }}>
        <pre
          aria-hidden="true"
          style={{
            ...codeText,
            position: 'sticky',
            left: 0,
            padding: '10px 10px 10px 12px',
            textAlign: 'right',
            color: '#9ca3af',
            background: '#f3f4f6',
            borderRight: '1px solid #e5e7eb',
            userSelect: 'none',
          }}>
          {lines.map((line, index) => index + 1).join('\n')}
        </pre>
        <pre style={{ ...codeText, flex: 1, padding: '10px 14px', color: '#111827' }}>{lines.join('\n')}</pre>
      </div>
    </div>
  )
}

// The code a run recorded (its TERMINAL_RUN, null when the run's record is missing), as its
// files (runFiles), or why there is none to show. `missing`: what to say when it was not
// recorded.
const RunCode = ({ run, missing }) => {
  const code = typeof run?.payload?.code === 'string' ? run.payload.code : null
  if (code === null) return <p style={dialogNote}>{missing}</p>
  if (!code.trim()) return <p style={dialogNote}>Their code was empty when they ran it.</p>
  return runFiles(code).map((file) => (
    <div key={file.name || 'code'} style={{ marginBottom: '10px' }}>
      {file.name && <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '4px' }}>{file.name}</div>}
      <CodeView code={file.code} />
    </div>
  ))
}

// What the Presenter gets for a run's code shown in CodeDialog (its `shown`) - { label, detail,
// code } for useExperimentPresenter's add, labelled 'Student 7 · NameError' or 'Student 2 ·
// Fails Sums a list' - or null when the run's code cannot go there (not recorded, empty, a web
// exercise's files). As the Overview has them: the Presenter filters them itself.
const presenterEntryOf = (shown) => {
  const code = presentableCode(shown?.run?.payload?.code)
  if (code == null) return null
  return shown.error
    ? { label: `${shown.label} · ${errorLabel(shown.about)}`, detail: shown.about, code }
    : { label: `${shown.label} · Fails ${shown.about}`, detail: shown.outcome, code }
}

// The same for a student's latest run, in their detail: `run` is the detail's (with its
// `outcome`), `label` the student's.
const latestRunEntryOf = (label, run) => {
  const code = presentableCode(run?.event?.payload?.code)
  if (code == null) return null
  const { outcome } = run
  if (outcome.error) {
    const key = errorKey(outcome.error.payload)
    return { label: `${label} · ${errorLabel(key)}`, detail: key, code }
  }
  if (outcome.result) {
    const [passed, total] = score(outcome.result)
    return { label: `${label} · Latest run`, detail: `${passed} of ${total} tests passed`, code }
  }
  return { label: `${label} · Latest run`, detail: outcome.clean ? 'Ran with no error' : '', code }
}

// A run's code, in a dialog: the run that raised one of a student's errors (opened from their
// detail), or the run behind their latest test result (Tests students are stuck on). The
// failing line is not marked in the code: the source a run executes has lines of its own
// before the student's code - a blank line with the tests off, the hidden-test setup's lines
// with them on (TerminalContext, testResults.hiddenSafePythonRun) - and the telemetry does not
// record which a run had, so the message's line numbers cannot be mapped onto the student's
// code for sure.
//   shown: { label, about, error, when, run, message, outcome }, kept while the dialog closes -
//     the student's label; what the code is shown for, an error's key (`error` true) or a
//     test's name, and when; the run (null when its record is missing); and the error's full
//     message, or else what the run came to (`outcome`: '1 of 3 tests passed'). The error's
//     key and message are the student's, shown through the word filter (filteredText); a
//     test's name is the instructor's.
//   onShowInPresenter(entry): when given, a Show in Presenter button puts the code there
//     (presenterEntryOf's entry), for a run whose code can go there.
const CodeDialog = ({ shown, open, onClose, onExited, onShowInPresenter }) => {
  const recorded = typeof shown?.run?.payload?.code === 'string'
  const message = String(shown?.message ?? '')
  const about = shown?.error ? filteredText(shown.about) : shown?.about
  const presenterEntry = onShowInPresenter ? presenterEntryOf(shown) : null
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onExited }}
      aria-labelledby="xp-code-title">
      {shown && (
        <>
          <DialogTitle id="xp-code-title" sx={{ pb: 0.5 }}>
            {shown.label}
          </DialogTitle>
          <DialogContent>
            <p style={{ ...cardSubtitle, margin: '0 0 16px 0' }}>
              <span style={shown.error ? { fontFamily: mono, color: '#991b1b' } : { color: '#111' }}>{about}</span> ·{' '}
              {shown.when}
            </p>
            <div style={{ ...statLabel, marginBottom: '6px' }}>Code they ran</div>
            <RunCode
              run={shown.run}
              missing={`The code of the run ${
                shown.error ? 'that raised this error' : 'behind this result'
              } was not recorded, so it cannot be shown.`}
            />
            {shown.error ? (
              <>
                <div style={{ ...statLabel, margin: '16px 0 6px 0' }}>Error message</div>
                <pre style={errorText}>{filteredText(message)}</pre>
                {recorded && /\bline \d+/.test(message) && (
                  <p style={{ ...cardSubtitle, margin: '8px 0 0 0' }}>
                    The line numbers in the message count lines the run adds before the student&apos;s code, so they
                    are not the line numbers above.
                  </p>
                )}
              </>
            ) : (
              <>
                <div style={{ ...statLabel, margin: '16px 0 6px 0' }}>Outcome</div>
                <p style={{ margin: 0, fontSize: '14px', color: '#111' }}>{shown.outcome}</p>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {presenterEntry && (
              <Button variant="outlined" onClick={() => onShowInPresenter(presenterEntry)}>
                Show in Presenter
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

// What became of a student's latest run (runOutcome), for their detail's 'Latest code'.
const OutcomeText = ({ outcome }) => {
  if (outcome.error) {
    return (
      <>
        Raised <span style={{ fontFamily: mono, color: '#991b1b' }}>{filteredText(errorKey(outcome.error.payload))}</span>
      </>
    )
  }
  if (outcome.result) {
    const [passed, total] = score(outcome.result)
    return `${passed} of ${total} tests passed`
  }
  return outcome.clean ? 'Ran with no error' : 'No outcome recorded yet'
}

const DetailStat = ({ label, children }) => (
  <div>
    <div style={statLabel}>{label}</div>
    <div style={{ fontSize: '18px', fontWeight: 600, color: '#111' }}>{children}</div>
  </div>
)

// A student's detail, in a dialog on the Overview rather than in their condition's Monitor
// (whose tab names the condition), as of the latest data: it follows each poll while open.
// Their flags, tests, time and AI questions; their errors, each opening the code of the run
// that raised it (CodeDialog, over this one); and the code of their latest run with what
// became of it. Their errors and code are shown through the word filter (filteredText).
//   detail: { row, result, errors, run, errorKey } (ExperimentOverviewTab's), kept while the
//     dialog closes; label: the student's; fetchedAt: when their telemetry was fetched;
//   onOpenError(error): show the code of one of their errors (studentErrors');
//   onShowInPresenter(entry): when given, a Show in Presenter button by their latest code puts
//     it there (latestRunEntryOf's entry).
// Three tabs (2026-11-02): Summary (all of the above), Conversation - their conversation with
// the AI so far (detail.conversation, StudentConversation), following each poll - and Replay -
// everything they did in their condition session, played back (StudentReplay; detail.replay
// names the session, null when they have none). The replay's session is taken when its tab
// first opens and kept until the dialog closes, and the replay stays loaded behind the other
// tabs, so going back keeps its place; a poll that names another session does not swap it. The
// dialog widens for the replay. Each student opens on Summary.
const StudentDialog = ({ detail, label, open, onClose, onExited, testsOn, fetchedAt, onOpenError, onShowInPresenter }) => {
  const row = detail?.row
  const summary = row?.summary
  const latestEntry = onShowInPresenter && detail?.run ? latestRunEntryOf(label, detail.run) : null
  const [tab, setTab] = useState('summary')
  // { studentId, sessionId, row } once the Replay tab has opened for this student.
  const [replayShown, setReplayShown] = useState(null)
  const studentId = detail?.id
  // Each opening - and another student while open (the Common errors card's rows) - starts on
  // Summary with no replay, decided while rendering so nothing from before mounts or loads.
  const openFor = open ? studentId ?? null : null
  const [openedFor, setOpenedFor] = useState(null)
  if (openFor !== openedFor) {
    setOpenedFor(openFor)
    if (openFor != null) {
      setTab('summary')
      setReplayShown(null)
    }
  }
  const showTab = (value) => {
    if (value === 'replay' && detail?.replay && replayShown?.studentId !== studentId) {
      setReplayShown({ studentId, sessionId: detail.replay.sessionId, row: detail.replay.row })
    }
    setTab(value)
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={tab === 'replay' ? 'xl' : 'md'}
      fullWidth
      TransitionProps={{ onExited }}
      aria-labelledby="xp-student-title">
      {detail && (
        <>
          <DialogTitle id="xp-student-title" sx={{ pb: 0 }}>
            {label}
          </DialogTitle>
          <Tabs value={tab} onChange={(event, value) => showTab(value)} aria-label={`${label}'s detail`} sx={{ px: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Tab value="summary" label="Summary" sx={{ textTransform: 'none' }} />
            <Tab value="conversation" label="Conversation" sx={{ textTransform: 'none' }} />
            <Tab value="replay" label="Replay" sx={{ textTransform: 'none' }} />
          </Tabs>
          <DialogContent sx={{ pt: 2 }}>
            {tab === 'conversation' && <StudentConversation items={detail.conversation} label={label} />}
            {replayShown?.studentId === studentId && (
              <div style={{ display: tab === 'replay' ? 'block' : 'none' }}>
                <StudentReplay sessionId={replayShown.sessionId} userId={studentId} row={replayShown.row} />
              </div>
            )}
            {tab === 'replay' && replayShown?.studentId !== studentId && <p style={dialogNote}>Nothing to replay yet.</p>}
            {tab === 'summary' && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '4px 0 16px 0' }}>
                  {row?.finished ? (
                    <span style={doneChip}>Passed all tests</span>
                  ) : row?.flags.length ? (
                    row.flags.map((flag) => (
                      <span key={flag.key} style={flagChip(flag.weight)}>
                        {flag.label}
                      </span>
                    ))
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>Nothing flagged</span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px 20px' }}>
                  {testsOn && <DetailStat label="Tests passed">{testsPassed(detail.result)}</DetailStat>}
                  <DetailStat label="Time on task">{formatMinutes(summary?.duration)}</DetailStat>
                  <DetailStat label="Last active">{formatLastActive(summary?.lastActive, row?.idleMs)}</DetailStat>
                  <DetailStat label="AI questions">{summary ? summary.chatCount : '—'}</DetailStat>
                </div>

                {testsOn && (
                  <>
                    <div style={sectionLabel}>
                      Tests in their latest result
                      {detail.result && ` · ${formatWhen(detail.result.event.timestamp, fetchedAt)}`}
                    </div>
                    {!detail.result ? (
                      <p style={dialogNote}>No test results yet.</p>
                    ) : !detail.result.tests.length ? (
                      <p style={dialogNote}>This result does not record which tests passed.</p>
                    ) : (
                      detail.result.tests.map((test, index) => (
                        <div
                          key={index}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ flex: 1, minWidth: 0, fontSize: '14px', color: '#111', wordBreak: 'break-word' }}>{test.name}</span>
                          <span style={test.passed ? doneChip : flagChip(3)}>{test.passed ? 'Passed' : 'Failing'}</span>
                        </div>
                      ))
                    )}
                  </>
                )}

                <div style={sectionLabel}>Errors</div>
                {!detail.errors.length ? (
                  <p style={dialogNote}>No errors.</p>
                ) : (
                  detail.errors.map((error) => (
                    <Box
                      key={error.key}
                      component="button"
                      type="button"
                      sx={{ ...listRowSx, ...(error.key === detail.errorKey ? { background: '#fef2f2' } : {}) }}
                      title="See the code they ran"
                      onClick={() => onOpenError(error)}>
                      <span style={rowText(true, { fontFamily: mono, color: '#991b1b' })}>{filteredText(error.key)}</span>
                      <span style={rowNote}>
                        {plural(error.count, 'time')} · {formatWhen(error.lastAt, fetchedAt)}
                      </span>
                      <span style={error.movedPast ? doneChip : flagChip(2)}>{error.movedPast ? 'Moved past' : 'Not yet'}</span>
                    </Box>
                  ))
                )}

                <div style={{ ...sectionLabel, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span>Latest code{detail.run && ` · ${formatWhen(detail.run.event.timestamp, fetchedAt)}`}</span>
                  {latestEntry && (
                    <button type="button" style={toolbarButton} onClick={() => onShowInPresenter(latestEntry)}>
                      Show in Presenter
                    </button>
                  )}
                </div>
                {!detail.run ? (
                  <p style={dialogNote}>No runs yet.</p>
                ) : (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#111' }}>
                      <OutcomeText outcome={detail.run.outcome} />
                    </p>
                    <RunCode run={detail.run.event} missing="The code of their latest run was not recorded, so it cannot be shown." />
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

// How many rows a card's list shows before 'Show all'.
const TOP_ROWS = 8

// A card's list whose rows open in place (Common errors, Tests students are stuck on): the
// first TOP_ROWS, with Show all. Each row is a button showing head(item, open); it opens to
// body(item).
const OpeningRows = ({ items, itemKey, head, body }) => {
  const [expanded, setExpanded] = useState(() => new Set())
  const [showAll, setShowAll] = useState(false)
  const toggle = (key) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const arrowSx = { fontSize: 18, color: '#9ca3af', flexShrink: 0 }

  return (
    <>
      {(showAll ? items : items.slice(0, TOP_ROWS)).map((item) => {
        const key = itemKey(item)
        const open = expanded.has(key)
        return (
          <div key={key}>
            <Box component="button" type="button" sx={listRowSx} aria-expanded={open} onClick={() => toggle(key)}>
              {open ? <KeyboardArrowDown sx={arrowSx} /> : <KeyboardArrowRight sx={arrowSx} />}
              {head(item, open)}
            </Box>
            {open && body(item)}
          </div>
        )
      })}
      {items.length > TOP_ROWS && (
        <button type="button" style={{ ...toolbarButton, marginTop: '12px' }} onClick={() => setShowAll(!showAll)}>
          {showAll ? `Show the top ${TOP_ROWS}` : `Show all ${items.length}`}
        </button>
      )}
    </>
  )
}

// The Common errors card: the class's errors grouped by message (commonErrors), the most
// students first. An error opens to the students who hit it - when they last did, and whether
// they have moved past it since - and a student to their detail (StudentDialog). An error is
// shown through the word filter (filteredText), on the rows shown; it is grouped as written.
//   groups: commonErrors'; label(id): a student's label; fetchedAtByUser: mergeConditions';
//   onSelect(group, entry): open that student's detail.
const CommonErrors = ({ loaded, groups, label, fetchedAtByUser, onSelect }) => (
  <div style={{ ...card, minWidth: 0 }}>
    <div style={cardHeader}>
      <h3 style={cardTitle}>Common errors</h3>
    </div>
    <p style={cardSubtitle}>
      Students&apos; errors with the same message together, the most students first. Click an error to see who hit it
      and whether they have moved past it since (a later run with no error, or more tests passing), and a student to
      see their detail.
    </p>
    {!loaded ? (
      <CircularProgress size={20} />
    ) : groups.length === 0 ? (
      <p style={emptyText}>No errors yet.</p>
    ) : (
      <OpeningRows
        items={groups}
        itemKey={(group) => group.key}
        head={(group, open) => {
          const latest = group.students.reduce((a, b) => (b.lastAt > a.lastAt ? b : a))
          const key = filteredText(group.key)
          return (
            <>
              <span title={open ? undefined : key} style={rowText(open, { fontFamily: mono })}>
                {key}
              </span>
              <span style={rowNote}>
                <span style={{ color: '#111', fontWeight: 600 }}>{plural(group.students.length, 'student')}</span>
                {' · '}
                {plural(group.occurrences, 'time')} · {formatWhen(latest.lastAt, fetchedAtByUser[latest.id])}
              </span>
            </>
          )
        }}
        body={(group) => (
          // m: the global table rule's 1.5em margin would open a gap under the error.
          <Table size="small" sx={{ ...tableSx, m: 0, mb: 1 }}>
            <TableBody>
              {group.students.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  title={`See ${label(entry.id)}'s detail`}
                  onClick={() => onSelect(group, entry)}>
                  <TableCell sx={{ ...oneLineSx, pl: '30px', width: '100%' }}>{label(entry.id)}</TableCell>
                  <TableCell sx={{ ...oneLineSx, color: '#6b7280' }} align="right">
                    {formatWhen(entry.lastAt, fetchedAtByUser[entry.id])}
                  </TableCell>
                  <TableCell sx={oneLineSx} align="right">
                    <span style={entry.movedPast ? doneChip : flagChip(2)}>{entry.movedPast ? 'Moved past' : 'Not yet'}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      />
    )}
  </div>
)

// The Tests students are stuck on card: each test someone is failing in their latest test
// result (stuckTests), the most students first - logic mistakes too, which raise no error. A
// test opens to its students, the longest failing first, and a student to the code of the run
// behind their latest result (CodeDialog).
//   stuck: stuckTests'; label(id): a student's label; fetchedAtByUser: mergeConditions';
//   onSelect(test, entry): show that student's code.
const StuckTests = ({ loaded, stuck, label, fetchedAtByUser, onSelect }) => {
  // How long a student has been failing a test, as of when their telemetry was fetched.
  const failingFor = (entry) => formatSpan((fetchedAtByUser[entry.id] ?? entry.latest.event.timestamp) - entry.since)
  return (
    <div style={{ ...card, minWidth: 0 }}>
      <div style={cardHeader}>
        <h3 style={cardTitle}>Tests students are stuck on</h3>
      </div>
      <p style={cardSubtitle}>
        For each test, how many of the students who have run the tests still fail it in their latest result - logic
        mistakes too, which raise no error - and how long the one failing it longest has been. Click a test to see who,
        and a student to see the code behind their latest result.
      </p>
      {!loaded ? (
        <CircularProgress size={20} />
      ) : !stuck.students ? (
        <p style={emptyText}>No test results yet.</p>
      ) : !stuck.tests.length ? (
        <p style={emptyText}>Every student who has run the tests passes all of them.</p>
      ) : (
        <OpeningRows
          items={stuck.tests}
          itemKey={(test) => test.name}
          head={(test, open) => (
            <>
              <span title={open ? undefined : test.name} style={rowText(open)}>
                {test.name}
              </span>
              <span style={rowNote}>
                <span style={{ color: '#111', fontWeight: 600 }}>
                  {test.failing.length} of {test.of}
                </span>{' '}
                still failing · longest {failingFor(test.failing[0])}
              </span>
            </>
          )}
          body={(test) => (
            <Table size="small" sx={{ ...tableSx, m: 0, mb: 1 }}>
              <TableBody>
                {test.failing.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    title={`See the code behind ${label(entry.id)}'s latest result`}
                    onClick={() => onSelect(test, entry)}>
                    <TableCell sx={{ ...oneLineSx, pl: '30px', width: '100%' }}>{label(entry.id)}</TableCell>
                    <TableCell sx={{ ...oneLineSx, color: '#6b7280' }} align="right">
                      for {failingFor(entry)}
                    </TableCell>
                    <TableCell sx={oneLineSx} align="right">
                      {score(entry.latest.event).join(' / ')} passed
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        />
      )}
    </div>
  )
}

// The experiment page's Overview: the class as a whole, every condition's students together,
// for watching the exercise as it runs - progress, errors, the tests students are stuck on, how
// students use the AI, who may need help. It may be projected, so nothing on it says which
// condition a student is in, nor who they are: a student is 'Student N' (studentNumbers), and
// their detail opens in a dialog here rather than in their condition's Monitor. The Compare
// conditions tab has the condition-by-condition view, with names. `conditions` are the
// config's conditions, each with `row` (its session row, polled by the page) when it still
// exists.
//
// The parts are the Monitor's own (ClassCodingTimeline, LatestConversation, the AI-use
// taxonomy), fed with every live condition's telemetry merged (mergeConditions), and the
// common errors and stuck tests from the same telemetry. All of it is rule-based: nothing on
// the Overview comes from an AI model, its exports included (the offloading analysis - its
// concepts, the Offloading CSV and its Session-level CSV columns - is on the Compare conditions
// tab). Until the first answer they show spinners, and while a condition's requests fail a
// notice says that some students' activity is missing - not whose.
//
// What students wrote - their AI questions, code, and errors' messages and keys - is shown
// through the project's word filter (filteredText), as the older group chat and code views
// show it; the conditions' Monitors show it as written.
//
// For the experiment page's instructors besides its creator, who have no condition tabs, the
// page leaves out what leads to them (each prop as before when given):
//   onOpenCompare: the header's Compare conditions button, only when given;
//   withExports: the exports (false: none - their files name each student's condition);
//   onShowInPresenter(entry): when given, a Show in Presenter button on a run's code (the code
//     dialog, and a student's latest code in their detail) hands it to the page's Presenter
//     ({ label: 'Student 7 · NameError', detail, code }, unfiltered: the Presenter filters it).
const ExperimentOverviewTab = ({ experimentId, config, conditions, onOpenCompare, withExports = true, onShowInPresenter }) => {
  const live = conditions.filter((condition) => condition.row)
  const { eventsBySession, rosterBySession, fetchedAtBySession, failedBySession, loaded } = useConditionTelemetry(
    live.map((condition) => Number(condition.sessionId))
  )

  const liveKey = live.map((condition) => `${condition.key}:${condition.sessionId}:${condition.aiChat ? 1 : 0}`).join(',')
  const merged = useMemo(
    () =>
      mergeConditions(
        live.map((condition) => ({ key: condition.key, sessionId: Number(condition.sessionId), aiChat: Boolean(condition.aiChat) })),
        eventsBySession,
        rosterBySession,
        fetchedAtBySession
      ),
    [liveKey, eventsBySession, rosterBySession, fetchedAtBySession] // eslint-disable-line react-hooks/exhaustive-deps
  )
  // Every condition runs the same exercise; with its test cases turned off nobody passes a test.
  const testsOn = live.some((condition) => testsEnabled(condition.row))
  // Each live condition's tests (its row's test_list), the same object while none changes: the
  // page re-reads the rows every few seconds.
  const testListsKey = JSON.stringify(
    live.map((condition) => [Number(condition.sessionId), Array.isArray(condition.row.test_list) ? condition.row.test_list : []])
  )
  const testListBySession = useMemo(() => Object.fromEntries(JSON.parse(testListsKey)), [testListsKey])
  // The class timeline's minutes count from the conditions' shared Start (their rows'
  // started_at), not from the first student's page: one opened before a Start, Reset, Start.
  // Not started yet: as before.
  const startedAtKey = live.map((condition) => condition.row.started_at || '').join(',')
  const startedAt = useMemo(() => {
    const times = startedAtKey.split(',').map((value) => Date.parse(value)).filter(Number.isFinite)
    return times.length ? Math.min(...times) : null
  }, [startedAtKey])
  const stats = useMemo(
    () => computeTelemetryStats(merged.eventsByUser, undefined, { startedAt }),
    [merged.eventsByUser, startedAt]
  )
  const patternsById = usePatternsById(merged.eventsByUser)
  const patterns = useMemo(() => aiUsePatterns(patternsById, merged.aiUserIds), [patternsById, merged.aiUserIds])
  const errors = useMemo(() => commonErrors({ ...merged, testsOn }), [merged, testsOn])
  const stuck = useMemo(() => stuckTests({ ...merged, testListBySession, testsOn }), [merged, testListBySession, testsOn])
  const numbers = useStudentNumbers(experimentId, merged.eventsByUser)
  const label = (id) => studentLabel(numbers, id)

  const connectedNow = live.reduce((sum, condition) => sum + (condition.row.active_stu || 0), 0)
  const status = classStatus({ stats, students: merged.students, aiUserIds: merged.aiUserIds, connectedNow })
  const rows = useMemo(
    () =>
      studentsByNeed({
        students: merged.students,
        stats,
        patternsById,
        homeByUser: merged.homeByUser,
        fetchedAtByUser: merged.fetchedAtByUser,
        testsOn,
        numbers,
        eventsByUser: merged.eventsByUser,
      }),
    [merged, stats, patternsById, testsOn, numbers]
  )
  // The Students list: a row for each student with telemetry, who all have a number; the rest
  // have not started.
  const started = rows.filter((row) => merged.eventsByUser[String(row.student.id)])
  const notStarted = rows.length - started.length
  // A row's Tests passed: the Monitor's figure (their summary's, from their last test result),
  // or for a student who has just started, with no summary yet, their latest test result's -
  // the same as their detail shows.
  const rowTestsPassed = ({ student, summary }) => {
    if (!summary) return testsPassed(latestEvent(merged.sessionsByUser[String(student.id)], 'TEST_CASE_RESULT'))
    return summary.testsTotal ? `${summary.testsPassed} / ${summary.testsTotal}` : '—'
  }

  // The student whose detail is open - { id, errorKey }, `errorKey` the error they were opened
  // from on the Common errors card - kept while the dialog closes, and whether it is open.
  const [detailShown, setDetailShown] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const openDetail = (id, fromError = null) => {
    setDetailShown({ id: String(id), errorKey: fromError })
    setDetailOpen(true)
  }
  // What their detail shows, from the latest data.
  const detail = useMemo(() => {
    if (!detailShown) return null
    const { id } = detailShown
    const sessions = merged.sessionsByUser[id] || {}
    const result = latestEvent(sessions, 'TEST_CASE_RESULT')
    const run = latestEvent(sessions, 'TERMINAL_RUN')
    return {
      ...detailShown,
      row: rows.find((row) => String(row.student.id) === id) || null,
      result: result && { ...result, tests: resultTests(result.event.payload, testListBySession[result.sessionId]) },
      // Their errors from their own events, as on the Common errors card (studentErrors), the
      // latest first - a student who has just started too, whom the card leaves out.
      errors: studentErrors(sessions, testsOn).sort((a, b) => b.lastAt - a.lastAt),
      run: run && { ...run, outcome: runOutcome(sessions[run.sessionId], run.index, testsOn) },
      // Their conversation with the AI so far (the Conversation tab).
      conversation: studentConversation(sessions),
    }
  }, [detailShown, merged.sessionsByUser, rows, testListBySession, testsOn])
  // The condition session the Replay tab plays: their home condition's (the one with their latest
  // activity, mergeConditions'), else one they have events in; null with none.
  const replayOf = (id) => {
    const sessions = merged.sessionsByUser[id] || {}
    const condition =
      live.find((candidate) => candidate.key === merged.homeByUser[id] && sessions[candidate.sessionId]) ||
      live.find((candidate) => sessions[candidate.sessionId])
    return condition ? { sessionId: Number(condition.sessionId), row: condition.row } : null
  }
  // The run whose code the code dialog shows (CodeDialog's `shown`), kept while it closes, and
  // whether it is open.
  const [codeShown, setCodeShown] = useState(null)
  const [codeOpen, setCodeOpen] = useState(false)
  const openCode = (shown) => {
    setCodeShown(shown)
    setCodeOpen(true)
  }
  // The code of student `id`'s error (studentErrors'), from their detail.
  const showError = (id, { key, lastAt, last }) =>
    openCode({
      label: label(id),
      about: key,
      error: true,
      when: formatWhen(lastAt, merged.fetchedAtByUser[id]),
      run: last.run,
      message: last.event.payload?.message,
    })
  const showResult = (test, entry) => {
    const [passed, total] = score(entry.latest.event)
    openCode({
      label: label(entry.id),
      about: test.name,
      error: false,
      when: `failing for ${formatSpan((merged.fetchedAtByUser[entry.id] ?? entry.latest.event.timestamp) - entry.since)}`,
      run: entry.latest.run,
      outcome: `${passed} of ${total} tests passed`,
    })
  }
  // A run's code to the page's Presenter, its dialogs closed (the page may open the Presenter).
  const showInPresenter = onShowInPresenter
    ? (entry) => {
        setCodeOpen(false)
        setDetailOpen(false)
        onShowInPresenter(entry)
      }
    : undefined

  const anyAiChat = live.some((condition) => condition.aiChat)
  const someWithoutAi = live.some((condition) => !condition.aiChat)
  // A live condition whose telemetry or roster failed last time: until it answers, its students
  // are missing from the figures, or shown as they were.
  const incomplete = live.some((condition) => failedBySession[Number(condition.sessionId)])

  // Whom each figure counts, since they differ.
  const aiCounted = `students with activity${someWithoutAi ? ' who had AI chat' : ''}`
  const countsNote = testsOn
    ? `Passed all tests counts students with activity; tests passed (avg), those who ran the tests${
        anyAiChat ? `; the AI figures, ${aiCounted}` : ''
      }.`
    : anyAiChat
      ? `The AI figures count ${aiCounted}.`
      : ''

  // What the Monitor's chart components read (TelemetryDataProvider's value).
  const telemetry = useMemo(
    () => ({
      eventsByUser: merged.eventsByUser,
      loading: !loaded,
      error: null,
      stats,
      getEventsForUser: (userId) => merged.eventsByUser[String(userId)] || [],
      refresh: () => {},
    }),
    [merged.eventsByUser, loaded, stats]
  )

  const loading = <CircularProgress size={20} />

  // The top of the page: the experiment, the Compare conditions tab and the exports.
  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
      <div>
        <div style={cardHeader}>
          <h3 style={cardTitle}>{config.name}</h3>
        </div>
        <p style={{ ...cardSubtitle, margin: 0 }}>
          {config.problemName && <>Exercise: {config.problemName} · </>}
          The whole class, every student together. Updated about every 20 seconds.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {onOpenCompare && (
          <button type="button" style={toolbarButton} onClick={onOpenCompare}>
            Compare conditions
          </button>
        )}
        {withExports && (
          <ExperimentExportButtons
            experimentId={experimentId}
            experimentName={config.name}
            conditions={conditions}
            withOffloading={false}
            withConditionNames={false}
          />
        )}
      </div>
    </div>
  )

  if (!live.length) {
    return (
      <div style={{ ...dashboardPage, height: 'auto', minHeight: '100%' }}>
        <div style={card}>
          {header}
          <p style={{ margin: '20px 0 0 0', color: '#9ca3af', fontSize: '14px' }}>
            Every condition&apos;s session was deleted from the session list, so there is no class to show.
          </p>
        </div>
      </div>
    )
  }

  return (
    <TelemetryDataContext.Provider value={telemetry}>
      <div style={{ ...dashboardPage, height: 'auto', minHeight: '100%' }}>
        {incomplete && <Alert severity="info">Some students&apos; activity could not be loaded. Retrying…</Alert>}
        <div style={card}>
          {header}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px 20px', marginTop: '20px' }}>
            <Stat label="Joined">{loaded ? status.joined : loading}</Stat>
            <Stat label="Connected now">{connectedNow}</Stat>
            {testsOn && (
              <Stat label="Passed all tests">
                {loaded ? (
                  <>
                    {status.passedAll} of {status.withActivity}
                    <span style={statNote}>{percent(status.passedAll, status.withActivity)}</span>
                  </>
                ) : (
                  loading
                )}
              </Stat>
            )}
            {testsOn && (
              <Stat label="Tests passed (avg)">
                {!loaded ? (
                  loading
                ) : status.avgTestsPassed == null ? (
                  '—'
                ) : (
                  <>
                    {Math.round(status.avgTestsPassed * 100)}%
                    <span style={statNote}>of {status.ranTests} who ran the tests</span>
                  </>
                )}
              </Stat>
            )}
            {anyAiChat && (
              <Stat label="Used the AI">
                {loaded ? (
                  <>
                    {status.ai.usedAi} of {status.ai.students}
                    <span style={statNote}>{percent(status.ai.usedAi, status.ai.students)}</span>
                  </>
                ) : (
                  loading
                )}
              </Stat>
            )}
            {anyAiChat && (
              <Stat label="AI questions per student">
                {loaded ? (
                  <>
                    {status.ai.perStudent == null ? '—' : status.ai.perStudent.toFixed(1)}
                    <span style={statNote}>{status.ai.questions} in all</span>
                  </>
                ) : (
                  loading
                )}
              </Stat>
            )}
          </div>
          {countsNote && <p style={{ ...cardSubtitle, margin: '16px 0 0 0' }}>{countsNote}</p>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(320px, 2fr)', gap: '20px', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
            <div style={{ height: '340px' }}>
              {loaded ? <ClassCodingTimeline /> : <Pending className="class-coding-timeline" title="Error Distribution" />}
            </div>
            <TimelineLegend />
          </div>

          <div style={card}>
            <div style={cardHeader}>
              <h3 style={cardTitle}>AI use patterns</h3>
            </div>
            {!anyAiChat ? (
              <p style={{ ...cardSubtitle, margin: 0 }}>Students in this experiment have no AI chat.</p>
            ) : !loaded ? (
              loading
            ) : (
              <>
                <p style={cardSubtitle}>
                  How the {patterns.students} students with AI chat and some activity used it: the Monitor&apos;s
                  student-sequence patterns, everyone together. A student can show several. Ask Without Trying counts
                  only the time before a question.
                </p>
                {patterns.patterns.map((pattern) => (
                  <PatternRow key={pattern.key} label={pattern.label} count={pattern.count} total={patterns.students} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Side by side when the tests are on, the errors alone when they are off. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '20px', alignItems: 'start' }}>
          <CommonErrors
            loaded={loaded}
            groups={errors}
            label={label}
            fetchedAtByUser={merged.fetchedAtByUser}
            onSelect={(group, entry) => openDetail(entry.id, group.key)}
          />
          {testsOn && (
            <StuckTests
              loaded={loaded}
              stuck={stuck}
              label={label}
              fetchedAtByUser={merged.fetchedAtByUser}
              onSelect={showResult}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(320px, 1fr)', gap: '20px', alignItems: 'start' }}>
          <div style={{ ...card, minWidth: 0 }}>
            <div style={cardHeader}>
              <h3 style={cardTitle}>Students</h3>
            </div>
            <p style={cardSubtitle}>
              Students who may need help first: {testsOn ? 'no test passing after 10 minutes, ' : ''}inactive for 5
              minutes, a debug loop, or asking the AI without trying. Students are numbered in the order they started.
              Click a student to see their detail.
            </p>
            {!loaded ? (
              loading
            ) : rows.length === 0 ? (
              <p style={emptyText}>No students have joined yet.</p>
            ) : (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Student</TableCell>
                      <TableCell sx={headSx}>Needs attention</TableCell>
                      {testsOn && <TableCell sx={headSx} align="right">Tests passed</TableCell>}
                      <TableCell sx={headSx} align="right">AI questions</TableCell>
                      <TableCell sx={headSx} align="right">Time on task</TableCell>
                      <TableCell sx={headSx} align="right">Last active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {started.map(({ student, summary, flags, idleMs, finished }) => (
                      <TableRow
                        key={student.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openDetail(student.id)}
                        title={`See ${label(student.id)}'s detail`}>
                        <TableCell sx={oneLineSx}>{label(student.id)}</TableCell>
                        <TableCell sx={cellSx}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {finished ? (
                              <span style={doneChip}>Passed all tests</span>
                            ) : flags.length ? (
                              flags.map((flag) => (
                                <span key={flag.key} style={flagChip(flag.weight)}>
                                  {flag.label}
                                </span>
                              ))
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </div>
                        </TableCell>
                        {testsOn && (
                          <TableCell sx={oneLineSx} align="right">
                            {rowTestsPassed({ student, summary })}
                          </TableCell>
                        )}
                        <TableCell sx={oneLineSx} align="right">{summary ? summary.chatCount : '—'}</TableCell>
                        <TableCell sx={oneLineSx} align="right">{formatMinutes(summary?.duration)}</TableCell>
                        <TableCell sx={oneLineSx} align="right">{formatLastActive(summary?.lastActive, idleMs)}</TableCell>
                      </TableRow>
                    ))}
                    {notStarted > 0 && (
                      <TableRow>
                        <TableCell sx={{ ...cellSx, color: '#9ca3af' }} colSpan={testsOn ? 6 : 5}>
                          {notStarted === 1 ? '1 student has' : `${notStarted} students have`} not started yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>

          {/* The questions alone, not who asked, through the word filter; without its modal
              (showAll), which lists students. */}
          <div style={{ height: '560px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {loaded ? (
              <LatestConversation showAll={false} formatText={filteredText} />
            ) : (
              <Pending className="latest-conversation" title="Latest Conversations" />
            )}
          </div>
        </div>
      </div>
      <StudentDialog
        detail={detail && { ...detail, replay: replayOf(detail.id) }}
        label={detail ? label(detail.id) : ''}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onExited={() => setDetailShown(null)}
        testsOn={testsOn}
        fetchedAt={detail ? merged.fetchedAtByUser[detail.id] : null}
        onOpenError={(error) => showError(detail.id, error)}
        onShowInPresenter={showInPresenter}
      />
      <CodeDialog
        shown={codeShown}
        open={codeOpen}
        onClose={() => setCodeOpen(false)}
        onExited={() => setCodeShown(null)}
        onShowInPresenter={showInPresenter}
      />
    </TelemetryDataContext.Provider>
  )
}

export default ExperimentOverviewTab
